import { useState, useEffect, useRef, useCallback } from "react";
import { api, timeAgo, errMsg } from "../lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { X, Camera } from "lucide-react";

const BRACKETS = [
  ["Student", "Small"], ["Student", "Medium"], ["Student", "Large"],
  ["Conventional", "Small"], ["Conventional", "Medium"], ["Conventional", "Large"],
  ["Support", "Home Office"],
];

function Card({ item, onOpen }) {
  const [rx, setRx] = useState(item.reactions);
  const [mine, setMine] = useState(item.my_reactions);

  const toggle = async (emoji) => {
    try {
      const { data } = await api.post(`/feed/${item.id}/react`, { emoji });
      setRx((r) => ({ ...r, [emoji]: data.count }));
      setMine((m) => (data.reacted ? [...m, emoji] : m.filter((e) => e !== emoji)));
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="sw-card bg-white rounded-none sm:rounded-lg overflow-hidden fade-up group" data-testid={`feed-card-${item.id}`}>
      <button className="w-full block overflow-hidden" onClick={() => onOpen(item)} data-testid={`feed-photo-${item.id}`}>
        {item.photo_thumb && <img src={item.photo_thumb} alt={item.task_name} className="w-full max-h-96 object-cover transition-transform duration-300 group-hover:scale-[1.03]" loading="lazy" />}
      </button>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="font-semibold text-[#1a1f36] text-sm">{item.task_name} <span className="text-[#c8102e] whitespace-nowrap">· {item.points} pts</span></div>
          <span className="text-xs text-slate-400 whitespace-nowrap shrink-0">{timeAgo(item.approved_at)}</span>
        </div>
        <div className="text-sm text-slate-500 mt-1 flex flex-wrap items-center gap-1.5">
          <span className="font-medium text-slate-700">{item.person}</span> {item.visited_property_name ? <>visited {item.visited_property_name}</> : <>at {item.property_name}</>}
          {item.person_role === "support" && !item.visited_property_name && <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded-full font-semibold text-xs" data-testid={`feed-support-badge-${item.id}`}>with Support Team</span>}
          {item.division && <span className="px-1.5 py-0.5 bg-slate-100 rounded-full font-semibold text-xs">{item.division === "Support" ? item.property_name : `${item.division}${item.tier ? ` · ${item.tier}` : ""}`}</span>}
        </div>
        <div className="flex gap-1.5 mt-3">
          {Object.keys(rx).map((emoji) => (
            <button key={emoji} onClick={() => toggle(emoji)}
              className={`inline-flex items-center justify-center min-h-11 min-w-11 sm:min-h-9 sm:min-w-9 px-2.5 rounded-full text-base sm:text-sm border transition-[transform,background-color,border-color] duration-150 hover:scale-110 active:scale-95 ${mine.includes(emoji) ? "border-[#c8102e] bg-red-50" : "border-slate-200 hover:bg-slate-50"}`}
              data-testid={`feed-react-${item.id}-${emoji}`}>
              {emoji}{rx[emoji] > 0 && <span className="text-xs font-semibold text-slate-600 ml-1">{rx[emoji]}</span>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function FeedFilters({ filter, setFilter }) {
  const chip = (label, active, onClick, testid) => (
    <button onClick={onClick} data-testid={testid}
      className={`min-h-11 sm:min-h-0 px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${active ? "bg-[#1a1f36] text-white border-[#1a1f36]" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
      {label}
    </button>
  );
  const bracketValue = filter.division && filter.tier ? `${filter.division}|${filter.tier}` : "";
  return (
    <div className="flex flex-wrap items-center gap-2 mb-6">
      {chip("All", !filter.division, () => setFilter({ division: "", tier: "" }), "feed-filter-all")}
      {chip("Student", filter.division === "Student" && !filter.tier, () => setFilter({ division: "Student", tier: "" }), "feed-filter-student")}
      {chip("Conventional", filter.division === "Conventional" && !filter.tier, () => setFilter({ division: "Conventional", tier: "" }), "feed-filter-conventional")}
      {chip("Support", filter.division === "Support" && !filter.tier, () => setFilter({ division: "Support", tier: "" }), "feed-filter-support")}
      <Select value={bracketValue} onValueChange={(v) => { const [d, t] = v.split("|"); setFilter({ division: d, tier: t }); }}>
        <SelectTrigger className="w-44 h-8 text-sm rounded-full" data-testid="feed-bracket-select"><SelectValue placeholder="Bracket…" /></SelectTrigger>
        <SelectContent>
          {BRACKETS.map(([d, t]) => <SelectItem key={`${d}|${t}`} value={`${d}|${t}`} data-testid={`feed-bracket-${d}-${t}`}>{d} · {t}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function Lightbox({ lightbox, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="feed-lightbox">
      <button className="absolute top-2 right-2 p-3 text-white/70 hover:text-white" data-testid="feed-lightbox-close"><X className="w-6 h-6" /></button>
      {lightbox.photo ? (
        <img src={lightbox.photo} alt={lightbox.task_name} className="max-h-full max-w-full rounded-md" />
      ) : lightbox.error ? (
        <p className="text-white/70 text-sm">Couldn't load the full-size photo — tap to close.</p>
      ) : (
        <p className="text-white/70 text-sm">Loading full size…</p>
      )}
    </div>
  );
}

export default function Feed() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ division: "", tier: "" });
  const [lightbox, setLightbox] = useState(null);
  const sentinel = useRef(null);

  const load = useCallback(async (p, f, reset) => {
    setLoading(true);
    const { data } = await api.get("/feed", { params: { page: p, division: f.division, tier: f.tier } });
    setItems((prev) => (reset ? data.items : [...prev, ...data.items]));
    setHasMore(data.has_more);
    setPage(p);
    setLoading(false);
  }, []);

  useEffect(() => { load(1, filter, true); }, [filter, load]);

  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loading) load(page + 1, filter, false);
    }, { rootMargin: "400px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loading, page, filter, load]);

  const openLightbox = async (item) => {
    setLightbox({ ...item, photo: null, error: false });
    try {
      const { data } = await api.get(`/feed/${item.id}/photo`);
      setLightbox((l) => (l && l.id === item.id ? { ...l, photo: data.photo } : l));
    } catch {
      setLightbox((l) => (l && l.id === item.id ? { ...l, error: true } : l));
    }
  };

  const chip = (label, active, onClick, testid) => (
    <button onClick={onClick} data-testid={testid}
      className={`min-h-11 sm:min-h-0 px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${active ? "bg-[#1a1f36] text-white border-[#1a1f36]" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
      {label}
    </button>
  );

  const bracketValue = filter.division && filter.tier ? `${filter.division}|${filter.tier}` : "";

  return (
    <div className="max-w-6xl mx-auto sm:px-4 py-6 sm:py-8">
      <div className="px-4 sm:px-0">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#c8102e] mb-2">Company-wide photo stream</p>
        <h1 className="font-display text-3xl font-bold text-[#1a1f36] mb-1">The Feed</h1>
        <p className="text-sm text-slate-600 mb-5">Every approved task photo, newest first. Reactions are for fun — they never affect scoring.</p>

        <FeedFilters filter={filter} setFilter={setFilter} />
      </div>

      {items.length === 0 && !loading ? (
        <div className="text-center py-20 mx-4 sm:mx-0 border border-dashed border-slate-300 rounded-lg" data-testid="feed-empty">
          <Camera className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No approved tasks yet — be the first on the feed.</p>
        </div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3 items-start" data-testid="feed-list">
          {items.map((item) => <Card key={item.id} item={item} onOpen={openLightbox} />)}
        </div>
      )}
      <div ref={sentinel} />
      {loading && <p className="text-center text-sm text-slate-400 py-6">Loading…</p>}
      {!hasMore && items.length > 0 && <p className="text-center text-xs text-slate-400 py-6">You're all caught up.</p>}

      {lightbox && <Lightbox lightbox={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}
